import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../images/logoo.png";

const navLinks = [
    { name: "Solutions", path: "/home" },
    { name: "Web", path: "/web-development" },
    { name: "Mobile", path: "/mobile-development" },
    { name: "AI/ML", path: "/ai-ml-solutions" },
    { name: "Docs", path: "/documentation" },
    { name: "Reviews", path: "/feedbacks" },
];

export default function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <nav
            className={`fixed top-4 md:top-8 left-1/2 -translate-x-1/2 z-[100] transition-all duration-700 w-[92%] md:w-[95%] max-w-7xl rounded-3xl md:rounded-[2.5rem] border ${scrolled
                ? "bg-[#020617]/80 backdrop-blur-3xl border-white/5 shadow-[0_25px_50px_rgba(0,0,0,0.5)] py-3 md:py-4 px-6 md:px-10"
                : "bg-slate-900/40/5 backdrop-blur-xl border-white/10 py-4 md:py-6 px-8 md:px-12"
                }`}
        >
            <div className="flex justify-between items-center">
                {/* Logo */}
                <Link to="/" className="flex items-center gap-4 group">
                    <div className="relative">
                        <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 scale-150" />
                        <img src={logo} alt="LAB DEVS" className="w-10 h-10 rounded-2xl relative z-10 border border-white/10 group-hover:rotate-12 group-hover:scale-110 transition-all duration-500 shadow-2xl" />
                    </div>
                    <span className="text-xl font-extrabold text-white tracking-tight uppercase leading-none">
                        LAB <br /><span className="text-blue-500 text-[10px] tracking-widest block -mt-0.5 font-black">DEVELOPERS</span>
                    </span>
                </Link>

                {/* Desktop Links */}
                <div className="hidden lg:flex items-center gap-10">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={`text-[10px] font-bold uppercase tracking-widest transition-all hover:text-blue-400 relative py-2 ${location.pathname === link.path ? "text-blue-400" : "text-slate-400"
                                }`}
                        >
                            {link.name}
                            {location.pathname === link.path && (
                                <motion.div
                                    layoutId="nav-underline"
                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                                />
                            )}
                        </Link>
                    ))}

                    <div className="w-px h-6 bg-slate-900/40/10 mx-4" />

                    <button
                        onClick={() => navigate("/contact")}
                        className="group relative px-8 py-3 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl overflow-hidden hover:scale-105 transition-all shadow-2xl"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="relative z-10">Get Started</span>
                    </button>
                </div>

                {/* Mobile Toggle */}
                <button
                    className="lg:hidden w-12 h-12 flex flex-col items-center justify-center bg-slate-900/40/5 rounded-2xl shadow-2xl border border-white/10"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                    <div className={`w-5 h-0.5 bg-slate-900/40 transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-1' : 'mb-1.5'}`} />
                    <div className={`w-5 h-0.5 bg-slate-900/40 transition-all duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-0.5' : ''}`} />
                </button>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        className="lg:hidden absolute top-full left-0 right-0 mt-4 bg-[#020617]/95 backdrop-blur-3xl rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden"
                    >
                        <div className="flex flex-col p-8 gap-8">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-blue-400 transition-all"
                                >
                                    {link.name}
                                </Link>
                            ))}
                            <button
                                onClick={() => {
                                    navigate("/contact");
                                    setMobileMenuOpen(false);
                                }}
                                className="w-full py-5 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl shadow-2xl"
                            >
                                Start Project
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
}
